import { useEffect, useState } from "react";
import {
  AccessTime as AccessTimeIcon,
  Add as AddIcon,
  CalendarMonth as CalendarMonthIcon,
  LocationOn as LocationOnIcon,
  NorthEast as NorthEastIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import api from "../api/api";

const upcomingEvents = [
  {
    title: "Alumni Leadership Summit",
    date: "2026-08-14",
    time: "6:00 PM - 9:00 PM",
    venue: "Einstein Convention Hall, Chennai",
    image: "/images/banner/Gemini_Generated_Image_v8lcp8v8lcp8v8lc.png",
    category: "Networking",
    description:
      "An evening focused on alumni leadership, strategic networking, and community collaboration with faculty, founders, and senior professionals.",
  },
  {
    title: "Startup & Innovation Showcase",
    date: "2026-08-29",
    time: "10:00 AM - 1:30 PM",
    venue: "Innovation Centre Auditorium, Bengaluru",
    image: "/images/enterprenur_opp.png",
    category: "Entrepreneurship",
    description:
      "A curated showcase of alumni-led ventures, product demos, and investor-ready stories designed to create visibility and meaningful partnerships.",
  },
  {
    title: "Career Acceleration Forum",
    date: "2026-09-12",
    time: "5:30 PM - 8:00 PM",
    venue: "Grand Forum, Hyderabad",
    image: "/images/job_refferal.png",
    category: "Careers",
    description:
      "Industry leaders and hiring managers share practical hiring insights, referral opportunities, and career growth strategies for members.",
  },
  {
    title: "Community Care Blood Donation Drive",
    date: "2026-09-26",
    time: "8:30 AM - 12:30 PM",
    venue: "Einstein Alumni Community Centre, Coimbatore",
    image: "/images/bllod_donation.png",
    category: "Community",
    description:
      "A well-organized donation drive that brings alumni together for a high-impact community initiative with volunteer coordination and medical support.",
  },
];

const eventHighlights = [
  "Curated alumni networking and speaker sessions",
  "Professionally presented event details with visual emphasis",
  "Clear venue, schedule, and event context for every listing",
];

const eventStatusOptions = ["Draft", "Published", "Cancelled", "Completed"];

const initialEventForm = {
  EventTitle: "",
  EventDescription: "",
  EventCategory: "",
  EventDate: "",
  StartTime: "",
  EndTime: "",
  VenueName: "",
  Address: "",
  City: "",
  State: "",
  BannerImage: null,
  EventStatus: "Draft",
  MaxParticipants: "",
  RegistrationRequired: "false",
  RegistrationStartDate: "",
  RegistrationEndDate: "",
  ContactPerson: "",
  ContactEmail: "",
  ContactPhone: "",
  EventUrl: "",
};

function formatEventDate(date) {
  return new Intl.DateTimeFormat("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(date));
}

function normalizeEvent(event) {
  const eventDate = event?.EventDate || event?.eventDate || event?.date;
  const startTime = event?.StartTime || event?.startTime || "";
  const endTime = event?.EndTime || event?.endTime || "";
  const bannerImage = event?.BannerImage || event?.bannerImage;

  return {
    title: event?.EventTitle || event?.eventTitle || event?.title || "Untitled Event",
    date: eventDate,
    time:
      startTime && endTime
        ? `${startTime} - ${endTime}`
        : startTime || endTime || "Time to be announced",
    venue:
      [event?.VenueName || event?.venueName, event?.City || event?.city, event?.State || event?.state]
        .filter(Boolean)
        .join(", ") || "Venue to be announced",
    image:
      bannerImage ||
      "/images/banner/Gemini_Generated_Image_x0npj0x0npj0x0np.png",
    category: event?.EventCategory || event?.eventCategory || event?.category || "General",
    description:
      event?.EventDescription ||
      event?.eventDescription ||
      event?.description ||
      "Event details will be published soon.",
    eventStatus:
      event?.EventStatus || event?.eventStatus || event?.status || "Upcoming",
  };
}

function EventCard({ event }) {
  return (
    <Card
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        boxShadow: "0 18px 48px rgba(15, 23, 42, 0.08)",
        border: "1px solid rgba(148, 163, 184, 0.18)",
        height: "100%",
      }}
    >
      <CardMedia
        component="img"
        height="240"
        image={event.image}
        alt={event.title}
        sx={{ objectFit: "cover" }}
      />
      <CardContent sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Chip
            label={event.category}
            sx={{
              bgcolor: "#eff6ff",
              color: "#1d4ed8",
              fontWeight: 700,
              borderRadius: 999,
            }}
          />
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {event.eventStatus}
          </Typography>
        </Stack>

        <Typography variant="h5" fontWeight={800} sx={{ mb: 1.25 }}>
          {event.title}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 1.75, mb: 2.5 }}
        >
          {event.description}
        </Typography>

        <Stack spacing={1.2} sx={{ mb: 2.5 }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <CalendarMonthIcon sx={{ color: "#2563eb", fontSize: 20 }} />
            <Typography variant="body2" fontWeight={600} color="#0f172a">
              {formatEventDate(event.date)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <AccessTimeIcon sx={{ color: "#2563eb", fontSize: 20 }} />
            <Typography variant="body2" fontWeight={600} color="#0f172a">
              {event.time}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <LocationOnIcon sx={{ color: "#2563eb", fontSize: 20 }} />
            <Typography variant="body2" fontWeight={600} color="#0f172a">
              {event.venue}
            </Typography>
          </Stack>
        </Stack>

        <Button
          variant="text"
          endIcon={<NorthEastIcon />}
          sx={{
            p: 0,
            minWidth: 0,
            textTransform: "none",
            fontWeight: 700,
          }}
        >
          View details
        </Button>
      </CardContent>
    </Card>
  );
}

function Events() {
  const { isAuthenticated, user } = useSelector((state) => state.user);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [eventForm, setEventForm] = useState(initialEventForm);
  const [eventList, setEventList] = useState([]);
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [eventsError, setEventsError] = useState("");

  const role = (user?.role || user?.Role || "").toString().toLowerCase();
  const isAdmin = isAuthenticated && role === "admin";
  const currentUserToken =
    user?.token || user?.accessToken || user?.jwtToken || "";

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    let isMounted = true;

    async function fetchEvents() {
      try {
        setIsLoadingEvents(true);
        setEventsError("");

        const response = await api.getEvents(currentUserToken);
        const events = Array.isArray(response?.data?.events)
          ? response.data.events
          : Array.isArray(response?.data?.data?.events)
            ? response.data.data.events
            : Array.isArray(response?.data)
              ? response.data
              : [];

        if (!isMounted) {
          return;
        }

        setEventList(
          events
            .map(normalizeEvent)
            .sort((left, right) => new Date(left.date) - new Date(right.date)),
        );
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setEventsError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load events right now.",
        );
        setEventList([]);
      } finally {
        if (isMounted) {
          setIsLoadingEvents(false);
        }
      }
    }

    fetchEvents();

    return () => {
      isMounted = false;
    };
  }, [currentUserToken]);

  const handleOpenDialog = () => {
    setFormError("");
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEventForm(initialEventForm);
    setFormError("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setEventForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleBannerImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    setEventForm((current) => ({
      ...current,
      BannerImage: file,
    }));
  };

  const handleSubmit = async () => {
    if (!eventForm.EventTitle.trim() || !eventForm.EventDate) {
      setFormError("Event title and event date are required.");
      return;
    }

    if (!currentUserToken) {
      setFormError("Logged-in session token is missing. Please sign in again.");
      return;
    }

    setIsSubmitting(true);
    setFormError("");

    try {
      const formData = new FormData();
      formData.append("EventTitle", eventForm.EventTitle.trim());
      formData.append("EventDate", eventForm.EventDate);

      [
        "EventDescription",
        "EventCategory",
        "StartTime",
        "EndTime",
        "VenueName",
        "Address",
        "City",
        "State",
        "EventStatus",
        "MaxParticipants",
        "RegistrationStartDate",
        "RegistrationEndDate",
        "ContactPerson",
        "ContactEmail",
        "ContactPhone",
        "EventUrl",
      ].forEach((field) => {
        if (eventForm[field]) {
          formData.append(field, eventForm[field]);
        }
      });

      formData.append(
        "RegistrationRequired",
        eventForm.RegistrationRequired === "true" ? "1" : "0",
      );

      if (eventForm.BannerImage) {
        formData.append("BannerImage", eventForm.BannerImage);
      }

      const response = await api.createEvent(formData, currentUserToken);
      const createdEvent =
        response?.data?.event ||
        response?.data?.data?.event ||
        response?.data?.data ||
        response?.data;

      setEventList((current) =>
        [...current, normalizeEvent(createdEvent)].sort(
          (left, right) => new Date(left.date) - new Date(right.date),
        ),
      );
      handleCloseDialog();
    } catch (error) {
      setFormError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to create event right now.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ py: { xs: 6, md: 9 } }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 5,
            color: "white",
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.96) 0%, rgba(37,99,235,0.92) 100%)",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.18)",
            mb: 5,
          }}
        >
          <Chip
            label="Upcoming Events"
            sx={{
              bgcolor: "rgba(255,255,255,0.14)",
              color: "white",
              fontWeight: 700,
              mb: 2,
            }}
          />
          <Typography
            variant="h3"
            fontWeight={800}
            sx={{ mb: 1.5, fontSize: { xs: "2rem", md: "3rem" } }}
          >
            Alumni events presented with clarity and credibility.
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255,255,255,0.86)",
              maxWidth: 760,
              lineHeight: 1.7,
            }}
          >
            Discover upcoming gatherings, professional forums, and community
            initiatives with complete event information including date, venue,
            timing, visuals, and context.
          </Typography>

          {isAdmin ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenDialog}
              sx={{
                mt: 3,
                borderRadius: 999,
                px: 2.5,
                py: 1.1,
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "white",
                color: "#0f172a",
                "&:hover": {
                  bgcolor: "#e2e8f0",
                },
              }}
            >
              Add Event
            </Button>
          ) : null}

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            divider={
              <Divider
                flexItem
                orientation="vertical"
                sx={{ borderColor: "rgba(255,255,255,0.16)" }}
              />
            }
            sx={{ mt: 3.5 }}
          >
            {eventHighlights.map((highlight) => (
              <Typography
                key={highlight}
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.82)", pr: { md: 2 } }}
              >
                {highlight}
              </Typography>
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: 3,
          }}
        >
          {isLoadingEvents ? (
            <Box
              sx={{
                gridColumn: "1 / -1",
                minHeight: 240,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : null}

          {!isLoadingEvents && eventsError ? (
            <Box
              sx={{
                gridColumn: "1 / -1",
                p: 3,
                borderRadius: 3,
                bgcolor: "#fef2f2",
                border: "1px solid #fecaca",
              }}
            >
              <Typography color="#b91c1c" fontWeight={600}>
                {eventsError}
              </Typography>
            </Box>
          ) : null}

          {!isLoadingEvents && !eventsError && !eventList.length ? (
            <Box
              sx={{
                gridColumn: "1 / -1",
                p: 3,
                borderRadius: 3,
                bgcolor: "#f8fafc",
                border: "1px solid rgba(148, 163, 184, 0.2)",
              }}
            >
              <Typography color="text.secondary" fontWeight={600}>
                No events available.
              </Typography>
            </Box>
          ) : null}

          {!isLoadingEvents && !eventsError
            ? eventList.map((event) => (
                <EventCard key={`${event.title}-${event.date}`} event={event} />
              ))
            : null}
        </Box>

        <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
          <DialogTitle>Add Event</DialogTitle>
          <DialogContent dividers>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: 2,
                pt: 1,
              }}
            >
              <TextField
                name="EventTitle"
                label="Event Title"
                value={eventForm.EventTitle}
                onChange={handleChange}
                required
                fullWidth
              />
              <TextField
                name="EventCategory"
                label="Event Category"
                value={eventForm.EventCategory}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                name="EventDate"
                label="Event Date"
                type="date"
                value={eventForm.EventDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: today }}
                required
                fullWidth
              />
              <TextField
                name="EventStatus"
                label="Event Status"
                value={eventForm.EventStatus}
                onChange={handleChange}
                select
                fullWidth
              >
                {eventStatusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                name="StartTime"
                label="Start Time"
                type="time"
                value={eventForm.StartTime}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                name="EndTime"
                label="End Time"
                type="time"
                value={eventForm.EndTime}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                name="VenueName"
                label="Venue Name"
                value={eventForm.VenueName}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                name="BannerImage"
                label="Banner Image"
                type="file"
                onChange={handleBannerImageChange}
                InputLabelProps={{ shrink: true }}
                inputProps={{ accept: "image/*" }}
                helperText={
                  eventForm.BannerImage
                    ? `Selected: ${eventForm.BannerImage.name}`
                    : "Upload an image file for the event banner."
                }
                fullWidth
              />
              <TextField
                name="City"
                label="City"
                value={eventForm.City}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                name="State"
                label="State"
                value={eventForm.State}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                name="MaxParticipants"
                label="Max Participants"
                type="number"
                value={eventForm.MaxParticipants}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                name="RegistrationRequired"
                label="Registration Required"
                value={eventForm.RegistrationRequired}
                onChange={handleChange}
                select
                fullWidth
              >
                <MenuItem value="false">No</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
              </TextField>
              <TextField
                name="RegistrationStartDate"
                label="Registration Start"
                type="datetime-local"
                value={eventForm.RegistrationStartDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                name="RegistrationEndDate"
                label="Registration End"
                type="datetime-local"
                value={eventForm.RegistrationEndDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                name="ContactPerson"
                label="Contact Person"
                value={eventForm.ContactPerson}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                name="ContactEmail"
                label="Contact Email"
                type="email"
                value={eventForm.ContactEmail}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                name="ContactPhone"
                label="Contact Phone"
                value={eventForm.ContactPhone}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                name="EventUrl"
                label="Event URL"
                value={eventForm.EventUrl}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                name="Address"
                label="Address"
                value={eventForm.Address}
                onChange={handleChange}
                multiline
                minRows={3}
                fullWidth
                sx={{ gridColumn: { xs: "1 / -1", sm: "1 / -1" } }}
              />
              <TextField
                name="EventDescription"
                label="Event Description"
                value={eventForm.EventDescription}
                onChange={handleChange}
                multiline
                minRows={4}
                fullWidth
                sx={{ gridColumn: { xs: "1 / -1", sm: "1 / -1" } }}
              />
            </Box>
            {formError ? (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                {formError}
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
              Save Event
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default Events;
